import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import type {
  StateResponse,
  CityResponse,
  CreateStateRequest,
  UpdateStateRequest,
  CreateCityRequest,
  UpdateCityRequest,
} from '@lov/shared';

// ============ STATE HOOKS ============

export function useStates() {
  return useQuery<StateResponse[]>({
    queryKey: ['states'],
    queryFn: async () => {
      return validatedApi.get('/locations/states');
    },
    staleTime: 1000 * 60 * 60, // 1 hour - locations rarely change
  });
}

export function useState(id: string) {
  return useQuery<StateResponse>({
    queryKey: ['states', id],
    queryFn: async () => {
      return validatedApi.get(`/locations/states/${id}`);
    },
    enabled: !!id,
  });
}

export function useCreateState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stateData: CreateStateRequest) => {
      return validatedApi.post('/locations/states', stateData);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['states'] });
    },
  });
}

export function useUpdateState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateStateRequest }) => {
      return validatedApi.put(`/locations/states/${id}`, data);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['states'] });
      void queryClient.invalidateQueries({ queryKey: ['states', variables.id] });
    },
  });
}

export function useDeleteState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await validatedApi.delete(`/locations/states/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['states'] });
    },
  });
}

// ============ CITY HOOKS ============

export function useCities(filters?: { stateId?: string; isPopular?: boolean; slug?: string }) {
  return useQuery<CityResponse[]>({
    queryKey: ['cities', filters],
    queryFn: async () => {
      return validatedApi.get('/locations/cities', { params: filters });
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

export function useCitiesByState(stateId: string) {
  return useQuery<CityResponse[]>({
    queryKey: ['cities', 'state', stateId],
    queryFn: async () => {
      return validatedApi.get(`/locations/states/${stateId}/cities`);
    },
    enabled: !!stateId,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

export function usePopularCities() {
  return useQuery<CityResponse[]>({
    queryKey: ['cities', 'popular'],
    queryFn: async () => {
      return validatedApi.get('/locations/cities/popular');
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

export function useCity(id: string) {
  return useQuery<CityResponse>({
    queryKey: ['cities', id],
    queryFn: async () => {
      return validatedApi.get(`/locations/cities/${id}`);
    },
    enabled: !!id,
  });
}

export function useCreateCity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cityData: CreateCityRequest) => {
      return validatedApi.post('/locations/cities', cityData);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cities'] });
      void queryClient.invalidateQueries({ queryKey: ['states'] }); // Refresh states with cities
    },
  });
}

export function useUpdateCity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCityRequest }) => {
      return validatedApi.put(`/locations/cities/${id}`, data);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['cities'] });
      void queryClient.invalidateQueries({ queryKey: ['cities', variables.id] });
      void queryClient.invalidateQueries({ queryKey: ['states'] });
    },
  });
}

export function useDeleteCity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await validatedApi.delete(`/locations/cities/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cities'] });
      void queryClient.invalidateQueries({ queryKey: ['states'] });
    },
  });
}
